import { useCallback, useEffect, useState } from "react";

type CommandType =
  | "BRIDGE_DIAGNOSTICS"
  | "AGORA_DISCOVERY"
  | "AGORA_CONNECTIVITY_PROBE";

interface BridgeCommand {
  id: string;
  type: CommandType;
  status: "PENDING" | "RUNNING" | "SUCCEEDED" | "FAILED";
  result: Record<string, unknown> | null;
  error: string | null;
  createdAt: string;
  completedAt: string | null;
}

interface BridgeAgent {
  bridgeId: string;
  bridgeVersion: string;
  machineName: string;
  platform: string;
  architecture: string;
  lastSeenAt: string;
  commands: BridgeCommand[];
}

const labels: Record<CommandType, string> = {
  BRIDGE_DIAGNOSTICS: "Diagnòstic del Bridge",
  AGORA_DISCOVERY: "Descobrir Àgora",
  AGORA_CONNECTIVITY_PROBE: "Comprovar ports d’Àgora",
};

const statusLabels: Record<BridgeCommand["status"], string> = {
  PENDING: "Pendent",
  RUNNING: "En execució",
  SUCCEEDED: "Completada",
  FAILED: "Fallida",
};

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body) headers.set("content-type", "application/json");
  const response = await fetch(url, {
    ...init,
    credentials: "include",
    headers,
  });
  const data = await response.json().catch(() => null) as
    | T
    | { message?: string; error?: string }
    | null;
  if (!response.ok) {
    const error = data as { message?: string; error?: string } | null;
    throw new Error(error?.message ?? error?.error ?? "Error de comunicació.");
  }
  return data as T;
}

function dateTime(value: string | null): string {
  return value ? new Intl.DateTimeFormat("ca-ES", {
    dateStyle: "short",
    timeStyle: "medium",
  }).format(new Date(value)) : "—";
}

export function BridgeMaintenancePanel() {
  const [agents, setAgents] = useState<BridgeAgent[]>([]);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    const response = await request<{ agents: BridgeAgent[] }>(
      "/api/v1/agora-bridge-admin/agents",
    );
    setAgents(response.agents);
  }, []);

  useEffect(() => {
    void load().catch((loadError: unknown) =>
      setError(loadError instanceof Error ? loadError.message : "No s’ha pogut consultar el Bridge."),
    );
    const timer = window.setInterval(() => void load().catch(() => undefined), 15_000);
    return () => window.clearInterval(timer);
  }, [load]);

  async function command(bridgeId: string, type: CommandType) {
    setBusy(`${bridgeId}:${type}`);
    setError("");
    setNotice("");
    try {
      await request(`/api/v1/agora-bridge-admin/agents/${bridgeId}/commands`, {
        method: "POST",
        body: JSON.stringify({ type }),
      });
      setNotice("Ordre segura enviada. El Bridge la recollirà en la propera connexió.");
      await load();
    } catch (commandError) {
      setError(commandError instanceof Error ? commandError.message : "No s’ha pogut enviar l’ordre.");
    } finally {
      setBusy("");
    }
  }

  return (
    <div className="bridge-maintenance-stack">
      {error && <p className="alert error">{error}</p>}
      {notice && <p className="alert success">{notice}</p>}
      <section className="panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">MANTENIMENT SEGUR</p>
            <h2>Bridge i connexió local amb Àgora</h2>
          </div>
          <span className="badge">Només lectura</span>
        </div>
        <p className="bridge-safety-note">
          Aquest canal no executa ordres lliures ni modifica Àgora. Les proves disponibles
          estan limitades, sanejades i auditades.
        </p>
        {agents.length === 0 && (
          <p className="empty-state">
            Encara no s’ha registrat cap Bridge 0.7.0. Instal·leu l’actualització al TPV.
          </p>
        )}
        {agents.map((agent) => (
          <article className="bridge-agent" key={agent.bridgeId}>
            <header>
              <div>
                <h3>{agent.machineName}</h3>
                <p>{agent.bridgeId} · Bridge v{agent.bridgeVersion}</p>
              </div>
              <span className="status-pill status-open">
                Vist: {dateTime(agent.lastSeenAt)}
              </span>
            </header>
            <p>{agent.platform} · {agent.architecture}</p>
            <div className="bridge-command-actions">
              {(Object.keys(labels) as CommandType[]).map((type) => (
                <button
                  className={type === "AGORA_DISCOVERY" ? "primary" : "secondary"}
                  type="button"
                  key={type}
                  disabled={Boolean(busy)}
                  onClick={() => void command(agent.bridgeId, type)}
                >
                  {busy === `${agent.bridgeId}:${type}` ? "Enviant…" : labels[type]}
                </button>
              ))}
            </div>
            <div className="bridge-command-history">
              <h4>Resultats recents</h4>
              {agent.commands.length === 0 && <p>Encara no s’ha executat cap prova.</p>}
              {agent.commands.map((item) => (
                <details key={item.id} className={`bridge-command bridge-command-${item.status.toLowerCase()}`}>
                  <summary>
                    <strong>{labels[item.type]}</strong>
                    <span>{statusLabels[item.status]}</span>
                    <time>{dateTime(item.completedAt ?? item.createdAt)}</time>
                  </summary>
                  {item.error && <p className="alert error">{item.error}</p>}
                  {item.result && <pre>{JSON.stringify(item.result, null, 2)}</pre>}
                </details>
              ))}
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}
